import {
  CircularProgress,
  CircularProgressLabel,
  Flex,
  FlexboxProps,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  CacheProjectState,
  CacheProjectStatus,
} from "libs/cache-project/types";
import { memo, useMemo } from "react";
import DownloadIcon from "./DownloadIcon";
import PauseIcon from "./PauseIcon";

interface Props {
  cacheProjectState: CacheProjectState | undefined;
  isDisabled: boolean;

  boxProps?: FlexboxProps;
}

const DownloadProjectIcon = (props: Props) => {
  const { isDisabled, cacheProjectState, boxProps } = props;

  const isStopping = cacheProjectState?.status === CacheProjectStatus.STOPPING;
  const isCaching =
    cacheProjectState?.status === CacheProjectStatus.PROCESSING || isStopping;
  const progress = cacheProjectState?.progress ?? 0;

  const color = useMemo(() => {
    if (isStopping) {
      return "var(--red)";
    }

    if (isCaching) {
      return "var(--yellow)";
    }

    return "var(--gray)";
  }, [isCaching, isStopping]);

  return (
    <Flex flexDirection="column" alignItems="center" {...boxProps}>
      <CircularProgress
        sx={{
          "& > svg": {
            width: "2.8rem",
            height: "2.8rem",
            fontSize: "2.8rem",
          },
        }}
        color={color}
        value={isCaching || progress !== 100 ? progress || 0 : 0}
      >
        <CircularProgressLabel
          display="flex"
          alignItems="center"
          justifyContent="center"
          title={`${progress}%`}
        >
          {isCaching ? (
            <PauseIcon width="1.9rem" height="1.9rem" />
          ) : (
            <DownloadIcon
              width="2.3rem"
              height="2.3rem"
              color="#171717"
              opacity={!isDisabled ? 1 : 0.5}
            />
          )}
        </CircularProgressLabel>
      </CircularProgress>

      {(isCaching || (progress > 0 && progress < 100)) && !isNaN(progress) && (
        <Text fontSize="1.2rem" color={color} lineHeight={1}>
          {`${progress}%`}
        </Text>
      )}
    </Flex>
  );
};

export default memo(DownloadProjectIcon);
